import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { g } from "@/lib/api";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Play, Pause, RotateCcw, Volume2, VolumeX, ArrowRight, Check, ChefHat, Utensils, Receipt, Users, Bike, Package, Wallet, Sparkles, AlertTriangle, CalendarClock, Phone as PhoneIcon } from "lucide-react";

// Durate di sicurezza: se la voce è attiva si aspetta la fine della frase, questi valgono da soli a voce spenta
const SCENE_MS = { prenotazioni: 9000, sala: 6500, comanda: 8000, cucina: 6500, pronto: 7000, conto: 7000,
                   asporto: 8000, magazzino: 8000, chiusura: 8500, prezzi: 8500 };
const ICONS = { prenotazioni: CalendarClock, sala: Users, comanda: Utensils, cucina: ChefHat, pronto: ChefHat, conto: Receipt,
                asporto: Bike, magazzino: Package, chiusura: Wallet, prezzi: Sparkles };

/** Telefono finto: dentro cambia la scena, così si capisce che si usa dal cellulare. */
const Phone = ({ children }) => (
  <div className="mx-auto w-full max-w-[300px] rounded-[2rem] border-[10px] border-slate-800 bg-slate-950 p-1 shadow-2xl">
    <div className="flex h-[440px] flex-col overflow-hidden rounded-[1.4rem] bg-slate-50">
      <div className="flex items-center gap-2 bg-slate-900 px-3 py-2 text-[10px] font-semibold text-white">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Osteria del Vicolo
        <span className="ml-auto text-slate-400">Sala</span>
      </div>
      <div className="flex-1 p-3">{children}</div>
      <div className="flex border-t border-slate-200 bg-white px-2 py-2 text-[9px] font-semibold text-slate-400">
        {["Sala", "Cassa", "Menu", "Altro"].map((v, i) => (
          <span key={v} className={cn("flex-1 text-center", i === 0 && "text-slate-900")}>{v}</span>
        ))}
      </div>
    </div>
  </div>
);

const Riga = ({ label, sub, right, active, done }) => (
  <div className={cn("flex items-center gap-2 rounded-lg border p-2.5 transition-all duration-500",
    done ? "border-emerald-200 bg-emerald-50" : active ? "border-amber-300 bg-amber-50 shadow-sm" : "border-slate-200 bg-white")}>
    <div className="min-w-0 flex-1">
      <p className="truncate text-xs font-semibold text-slate-900">{label}</p>
      {sub && <p className="truncate text-[10px] text-slate-500">{sub}</p>}
    </div>
    {done ? <Check className="h-4 w-4 shrink-0 text-emerald-600" />
          : <span className="font-num shrink-0 text-xs font-bold text-slate-700">{right}</span>}
  </div>
);

const Scena = ({ id, t }) => {
  if (id === "prenotazioni")
    return (
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cf.scene.prenotazioni")}</p>
        <div className="flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2.5 text-white">
          <PhoneIcon className="h-3.5 w-3.5" />
          <span className="text-[10px] font-semibold">{t("cf.book.incoming")}</span>
        </div>
        <Riga label="Rossi · 4 pax" sub={`20:30 · ${t("cf.book.confirmed")}`} right="S3" active />
        <Riga label="Bianchi · 2 pax" sub={`21:00 · ${t("cf.book.confirmed")}`} right="S1" />
        <Riga label="Verdi · 6 pax" sub={`21:30 · ${t("cf.book.waiting")}`} right="—" />
        <div className="mt-3 rounded-lg border border-slate-200 bg-white py-2.5 text-center text-[10px] font-bold text-slate-900">
          {t("cf.book.badge")}
        </div>
      </div>
    );
  if (id === "sala")
    return (
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cf.scene.sala")}</p>
        <div className="grid grid-cols-2 gap-2">
          {["S1", "S2", "S3", "S4"].map((n, i) => (
            <div key={n} className={cn("rounded-lg border p-3 text-center transition-all duration-700",
              i === 2 ? "scale-105 border-amber-400 bg-amber-50 shadow-md" : "border-slate-200 bg-white")}>
              <p className="text-sm font-bold">{n}</p>
              <p className="text-[10px] text-slate-500">{i === 2 ? t("cf.tap") : t("cf.free")}</p>
            </div>
          ))}
        </div>
      </div>
    );
  if (id === "comanda")
    return (
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cf.scene.comanda")}</p>
        <Riga label="Carbonara" sub={t("cf.course1")} right="11,00" active />
        <Riga label="Margherita" sub={t("cf.course2")} right="7,00" active />
        <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-900 px-3 py-2.5 text-white">
          <span className="text-[10px] font-semibold">2 × · 18,00 €</span>
          <span className="text-[10px] font-bold">{t("cf.send")}</span>
        </div>
      </div>
    );
  if (id === "cucina" || id === "pronto")
    return (
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cf.scene.cucina")}</p>
        <Riga label="Carbonara · S3" sub={id === "pronto" ? t("cf.ready") : t("cf.cooking")} right={id === "pronto" ? "" : "2'"} active={id === "cucina"} done={id === "pronto"} />
        <Riga label="Margherita · S3" sub={id === "pronto" ? t("cf.ready") : t("cf.oven")} right={id === "pronto" ? "" : "4'"} active={id === "cucina"} done={id === "pronto"} />
        {id === "pronto" && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2.5 text-white">
            <Volume2 className="h-3.5 w-3.5" />
            <span className="text-[10px] font-semibold">{t("cf.voice")}</span>
          </div>
        )}
      </div>
    );
  if (id === "asporto")
    return (
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cf.scene.asporto")}</p>
        <Riga label="Marco R." sub={`${t("cf.pickup")} 20:30 · 2 ${t("cf.items")}`} right="18,00" active />
        <Riga label="Giulia B." sub={`${t("cf.delivery")} · Via Roma 12`} right="24,50" />
        <Riga label="Luca P." sub={`${t("cf.pickup")} 21:00`} right="12,00" done />
        <div className="mt-3 rounded-lg bg-slate-900 px-3 py-2.5 text-center text-[10px] font-bold text-white">
          {t("cf.asporto.badge")}
        </div>
      </div>
    );
  if (id === "magazzino")
    return (
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cf.scene.magazzino")}</p>
        <Riga label="Guanciale" sub={t("cf.stock.low")} right="0,8 kg" active />
        <Riga label="Mozzarella" sub={t("cf.stock.ok")} right="6,4 kg" />
        <Riga label="Farina 00" sub={t("cf.stock.ok")} right="24 kg" />
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2.5 text-slate-950">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span className="text-[10px] font-bold">{t("cf.stock.alert")}</span>
        </div>
      </div>
    );
  if (id === "chiusura")
    return (
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cf.scene.chiusura")}</p>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          {[[t("cf.cashline"), "412,00"], [t("cf.cardline"), "738,50"], [t("cf.float"), "150,00"]].map(([n, p]) => (
            <div key={n} className="flex justify-between border-b border-slate-100 py-1.5 text-[11px] last:border-0">
              <span className="text-slate-600">{n}</span><span className="font-num font-semibold">{p}</span>
            </div>
          ))}
          <div className="mt-2 flex justify-between text-sm font-bold"><span>{t("cf.dayTotal")}</span><span className="font-num">1.150,50 €</span></div>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2.5 text-white">
          <Check className="h-3.5 w-3.5" />
          <span className="text-[10px] font-semibold">{t("cf.balanced")}</span>
        </div>
      </div>
    );
  if (id === "prezzi")
    return (
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cf.scene.prezzi")}</p>
        {[t("cf.plan.f1"), t("cf.plan.f2"), t("cf.plan.f3")].map((s) => (
          <div key={s} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-2.5">
            <Check className="h-4 w-4 shrink-0 text-emerald-600" />
            <p className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-900">{s}</p>
          </div>
        ))}
        <div className="mt-3 rounded-lg border border-amber-400 bg-amber-50 px-3 py-2.5 text-center">
          <p className="text-sm font-bold text-slate-900">{t("cf.plan.freeNow")}</p>
        </div>
        <div className="mt-3 rounded-lg bg-slate-900 px-3 py-2.5 text-center text-[10px] font-bold text-white">
          {t("cf.plan.note")}
        </div>
      </div>
    );
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cf.scene.conto")}</p>
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        {[["Carbonara", "11,00"], ["Margherita", "7,00"], ["Acqua 1L", "2,50"]].map(([n, p]) => (
          <div key={n} className="flex justify-between border-b border-slate-100 py-1.5 text-[11px] last:border-0">
            <span className="text-slate-600">{n}</span><span className="font-num font-semibold">{p}</span>
          </div>
        ))}
        <div className="mt-2 flex justify-between text-sm font-bold"><span>{t("cf.total")}</span><span className="font-num">20,50 €</span></div>
      </div>
      <div className="flex gap-2">
        <div className="flex-1 rounded-lg bg-slate-900 py-2.5 text-center text-[10px] font-bold text-white">{t("cf.cash")}</div>
        <div className="flex-1 rounded-lg border border-slate-300 py-2.5 text-center text-[10px] font-bold">{t("cf.split")}</div>
      </div>
    </div>
  );
};

export default function ComeFunziona() {
  const { t, lang } = useI18n();
  const [steps, setSteps] = useState([]);
  const stepsRef = useRef([]);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  const audio = useRef(null);
  const player = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    g(`/public/demo-voice?lang=${lang}`)
      .then((d) => {
        stepsRef.current = d.steps || [];
        setSteps(d.steps || []);
      })
      .catch(() => setSteps([]));
  }, [lang]);

  const step = steps[i];
  const backend = process.env.REACT_APP_BACKEND_URL;

  const stop = useCallback(() => {
    clearTimeout(timer.current);
    if (player.current) player.current.pause();
  }, []);

  // iPhone autorizza solo il lettore avviato durante il tocco: ne teniamo uno solo e cambiamo traccia
  const say = useCallback((url) => {
    if (mutedRef.current || !url || !player.current) return;
    const el = player.current;
    el.pause();
    el.src = `${backend}${url}`;
    el.currentTime = 0;
    el.play().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // L'avanzamento dipende solo dal passo: un ricaricamento dell'elenco non azzera più il tempo
  useEffect(() => {
    if (!playing || !steps.length) return;
    const s = stepsRef.current[i];
    if (!s) return;
    if (i > 0) say(s.url);  // il passo 0 parte dal tocco su «Avvia»
    // Scarico in anticipo la voce della scena successiva: il passaggio non si inceppa più
    const next = stepsRef.current[i + 1];
    if (next?.url) fetch(`${backend}${next.url}`, { cache: "force-cache" }).catch(() => {});

    const avanti = () => {
      if (i + 1 < stepsRef.current.length) setI(i + 1);
      else setPlaying(false);
    };
    // La frase non viene più tagliata: si cambia scena poco dopo che la voce ha finito
    const el = player.current;
    const onEnd = () => { clearTimeout(timer.current); timer.current = setTimeout(avanti, 700); };
    if (el && !mutedRef.current) el.addEventListener("ended", onEnd);
    // Rete di sicurezza se l'audio non parte o è silenziato
    timer.current = setTimeout(avanti, (SCENE_MS[s.id] || 7000) + (mutedRef.current ? 0 : 4000));
    return () => {
      clearTimeout(timer.current);
      if (el) el.removeEventListener("ended", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, i, steps.length]);

  useEffect(() => stop, [stop]);

  // Il primo tocco crea e sblocca il lettore: da lì in poi iPhone lo lascia parlare a ogni scena
  const start = () => {
    if (!player.current) {
      const el = new Audio();
      el.preload = "auto";
      player.current = el;
    }
    const first = stepsRef.current[0];
    setI(0);
    setPlaying(true);
    if (!mutedRef.current && first?.url) {
      player.current.src = `${backend}${first.url}`;
      player.current.play().catch(() => {});
    }
  };
  const dots = useMemo(() => steps.map((s) => s.id), [steps]);

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="come-funziona-page">
      <div className="mx-auto max-w-5xl px-6 py-14 lg:py-20">
        <a href="/" className="text-xs font-semibold text-slate-400 hover:text-amber-400" data-testid="cf-back">← {t("cf.back")}</a>
        <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.3em] text-amber-400">{t("cf.overline")}</p>
        <h1 className="font-head mt-3 text-4xl font-bold sm:text-5xl">{t("cf.title")}</h1>
        <p className="mt-4 max-w-xl text-base text-slate-300">{t("cf.sub")}</p>

        <div className="mt-10 grid gap-10 lg:grid-cols-2 lg:items-center">
          <Phone>{step ? <Scena id={step.id} t={t} /> : <Scena id="sala" t={t} />}</Phone>

          <div>
            <div className="flex items-center gap-1.5" data-testid="cf-progress">
              {dots.map((id, n) => (
                <span key={id} className={cn("h-1.5 flex-1 rounded-full transition-colors",
                  n <= i && playing ? "bg-amber-400" : n <= i ? "bg-slate-600" : "bg-slate-800")} />
              ))}
            </div>

            <div className="mt-6 min-h-[110px] rounded-xl border border-slate-800 bg-slate-900/60 p-5" data-testid="cf-subtitle">
              {step && (
                <>
                  <div className="flex items-center gap-2 text-amber-400">
                    {(() => { const I = ICONS[step.id]; return <I className="h-4 w-4" />; })()}
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em]">
                      {t(`cf.step.${step.id}`)} · {i + 1}/{steps.length}
                    </span>
                  </div>
                  <p className="mt-3 text-lg leading-relaxed text-slate-100">{step.text}</p>
                </>
              )}
              {!step && <p className="text-sm text-slate-400">{t("cf.loading")}</p>}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {!playing ? (
                <Button className="h-14 bg-amber-500 px-7 text-base font-bold text-slate-950 hover:bg-amber-400"
                        onClick={start} data-testid="cf-play-btn">
                  <Play className="mr-2 h-5 w-5" /> {i === 0 ? t("cf.play") : t("cf.resume")}
                </Button>
              ) : (
                <Button className="h-14 bg-slate-800 px-7 text-base font-bold text-white hover:bg-slate-700"
                        onClick={() => { setPlaying(false); stop(); }} data-testid="cf-pause-btn">
                  <Pause className="mr-2 h-5 w-5" /> {t("cf.pause")}
                </Button>
              )}
              <Button variant="ghost" className="h-14 text-slate-300 hover:bg-slate-900 hover:text-white"
                      onClick={() => {
                        const next = !muted;
                        mutedRef.current = next;
                        setMuted(next);
                        if (player.current) player.current.pause();
                        if (!next && playing) say(stepsRef.current[i]?.url);
                      }} data-testid="cf-mute-btn">
                {muted ? <VolumeX className="mr-2 h-5 w-5" /> : <Volume2 className="mr-2 h-5 w-5" />}
                {muted ? t("cf.unmute") : t("cf.mute")}
              </Button>
              <Button variant="ghost" className="h-14 text-slate-300 hover:bg-slate-900 hover:text-white"
                      onClick={() => { stop(); start(); }} data-testid="cf-restart-btn">
                <RotateCcw className="mr-2 h-5 w-5" /> {t("cf.again")}
              </Button>
            </div>

            <p className="mt-4 text-xs text-slate-500">{t("cf.note")}</p>

            <div className="mt-8 rounded-xl border border-slate-800 bg-slate-900/60 p-5">
              <p className="text-sm text-slate-300">{t("cf.cta.sub")}</p>
              <Button className="mt-4 h-14 w-full bg-white px-7 text-base font-bold text-slate-950 hover:bg-slate-200"
                      onClick={() => { window.location.href = "/prova"; }} data-testid="cf-trial-btn">
                {t("cf.cta")} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
